import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Printer, AlertCircle, Download } from 'lucide-react';
import { BillingInfo, PaymentRecord } from '../lib/billing-hooks';
export function InvoicePage() {
  const {
    paymentId
  } = useParams<{
    paymentId: string;
  }>();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [tableLabel, setTableLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!paymentId) return;
      try {
        const {
          data: pay,
          error: payErr
        } = await supabase.from('payments').select('*').eq('id', paymentId).maybeSingle();
        if (payErr) throw payErr;
        if (!pay) throw new Error('Factura no encontrada');
        const {
          data: billing,
          error: bErr
        } = await supabase.from('restaurant_billing_info').select('*').eq('restaurant_id', pay.restaurant_id).maybeSingle();
        if (bErr) throw bErr;
        if (!billing) throw new Error('Datos del comercio no disponibles');
        let tLabel: string | null = null;
        if (pay.table_id) {
          const {
            data: tbl
          } = await supabase.from('tables').select('table_number, table_name').eq('id', pay.table_id).maybeSingle();
          if (tbl) tLabel = tbl.table_name || `Mesa ${tbl.table_number}`;
        }
        if (mounted) {
          setPayment(pay as PaymentRecord);
          setInfo(billing as BillingInfo);
          setTableLabel(tLabel);
        }
      } catch (err: any) {
        if (mounted) setError(err.message || 'Error al cargar la factura');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [paymentId]);
  const fmt = (n: number) => new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0
  }).format(n);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-5050">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-5051" />
      </div>;
  }
  if (error || !payment || !info) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6" data-id="element-5052">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg" data-id="element-5053">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-5054" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-5055">
            Factura no disponible
          </h1>
          <p className="text-sm text-gray-500" data-id="element-5056">
            {error || 'No se pudo cargar el documento.'}
          </p>
        </div>
      </div>;
  }
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0" data-id="element-5057">
      <style data-id="element-5058">{`
        @media print {
          @page { margin: 8mm; size: 80mm auto; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-sm mx-auto" data-id="element-5059">
        {/* Action bar */}
        <div className="no-print flex gap-2 mb-4" data-id="element-5060">
          <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-lg hover:bg-gray-800 transition-colors" data-id="element-5061">
            <Printer className="w-4 h-4" data-id="element-5062" />
            Imprimir / Guardar PDF
          </button>
        </div>

        <p className="no-print text-xs text-gray-500 text-center mb-4" data-id="element-5063">
          💡 En el diálogo de impresión, elige{' '}
          <strong data-id="element-5064">"Guardar como PDF"</strong> para descargar la factura.
        </p>

        {/* Receipt */}
        <div className="bg-white p-5 shadow-md font-mono text-gray-900 border border-gray-200 print:border-0 print:shadow-none" data-id="element-5065">
          <h1 className="text-base font-black text-center tracking-wide mb-1" data-id="element-5066">
            {info.business_name}
          </h1>
          <div className="text-[11px] text-center leading-snug mb-3" data-id="element-5067">
            <strong className="block" data-id="element-5068">
              Cédula {info.legal_id_type === 'fisica' ? 'Física' : 'Jurídica'}:{' '}
              {info.legal_id}
            </strong>
            {info.address && <>
                {info.address}
                <br data-id="element-5069" />
              </>}
            {info.phone && <>Tel: {info.phone}</>}
            {info.email && <> · {info.email}</>}
          </div>

          <div className="text-[11px] border-y border-dashed border-gray-400 py-2 my-2 space-y-0.5" data-id="element-5070">
            <div className="flex justify-between" data-id="element-5071">
              <span data-id="element-5072">Factura N°</span>
              <strong data-id="element-5073">
                {consecStr}
                {payment.is_partial && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px] font-bold" data-id="element-5074">
                    PARCIAL
                  </span>}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-5075">
              <span data-id="element-5076">Fecha</span>
              <span data-id="element-5077">{date}</span>
            </div>
            {tableLabel && <div className="flex justify-between" data-id="element-5078">
                <span data-id="element-5079">Mesa</span>
                <span data-id="element-5080">{tableLabel}</span>
              </div>}
          </div>

          <table className="w-full text-[11px] my-2" data-id="element-5081">
            <thead data-id="element-5082">
              <tr className="border-b border-gray-900 uppercase text-[10px]" data-id="element-5083">
                <td data-id="element-5084">Cant</td>
                <td data-id="element-5085">Descripción</td>
                <td className="text-right" data-id="element-5086">Total</td>
              </tr>
            </thead>
            <tbody data-id="element-5087">
              {(payment.items_json || []).map((it, idx) => <tr key={idx} data-id="element-5088">
                  <td className="font-bold w-8" data-id="element-5089">{it.quantity}×</td>
                  <td className="pr-2" data-id="element-5090">
                    {it.item_name}
                    {it.notes && <div className="text-[10px] text-gray-500 italic" data-id="element-5091">
                        {it.notes}
                      </div>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap" data-id="element-5092">
                    {fmt(it.line_total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          <div className="border-t border-dashed border-gray-400 pt-2 mt-2" data-id="element-5093">
            <div className="flex justify-between text-sm font-black border-t-2 border-gray-900 pt-2 mt-1" data-id="element-5094">
              <span data-id="element-5095">TOTAL</span>
              <span data-id="element-5096">{fmt(payment.amount)}</span>
            </div>
          </div>

          <div className="flex justify-between text-[11px] border-t border-dashed border-gray-400 pt-2 mt-2" data-id="element-5097">
            <span data-id="element-5098">Forma de pago</span>
            <strong data-id="element-5099">{methodLabel}</strong>
          </div>

          {info.footer_note && <p className="text-center text-[11px] mt-3 text-gray-700" data-id="element-5100">
              {info.footer_note}
            </p>}
          <p className="text-center text-[9px] mt-2 text-gray-500 leading-tight" data-id="element-5101">
            Régimen Tributario Simplificado
            <br data-id="element-5102" />
            Documento equivalente — no constituye factura electrónica
          </p>
        </div>
      </div>
    </div>;
}